
import { GetterTree } from 'vuex';
import { GspModuleState, ServiceModuleGroup } from './types';
import { RootState } from '../../types';
import { GspModule } from '@/shared/model/gspModule';
import { GSP_MODULE_CACHE_CONFIG as CACHE_CONFIG } from '@/shared/config/cache';

export const getters: GetterTree<GspModuleState, RootState> = {
  ['getGspModules']: (state) => state.gspModules,
  ['getGspModulesIsLoading']: (state) => state.gspModules?.isLoading,
  ['getGspModulesTotal']: (state) => state.gspModules?.total,
  ['getGspModulesSearchParams']: (state) => state.gspModules?.searchParams,

  ['isAllGspModulesCacheValid']: (state): boolean => {
    if (!state.allGspModulesCache.lastFetchTime || !state.allGspModulesCache.isValid) return false;
    const elapsed = Date.now() - state.allGspModulesCache.lastFetchTime;
    return elapsed < CACHE_CONFIG.DURATION;
  },

  ['getAllGspModulesCacheInfo']: (state) => ({
    isValid: state.allGspModulesCache.isValid,
    lastFetchTime: state.allGspModulesCache.lastFetchTime,
    ageInSeconds: state.allGspModulesCache.lastFetchTime
      ? Math.floor((Date.now() - state.allGspModulesCache.lastFetchTime) / 1000)
      : null,
  }),

  ['getCacheStatus']: (state, getters) => ({
    main: getters.getAllGspModulesCacheInfo,
    grouped: getters.getModulesGroupedCacheInfo,
  }),


  /**
 * Get grouped modules by service
 * Computed on-the-fly from gspModules.items
 * Returns: [{ serviceId: 1, moduleCount: 5, commonCount: 12, specificCount: 4 }, ...]
 */
  ['getModulesGroupedByService']: (state): ServiceModuleGroup[] => {
    const ignoredModules = [
      { serviceId: 1, name: 'Blockverrechnung' },
      { serviceId: 1, name: 'Spotmarkt' },
    ];

    const filteredModules = state.allGspModules.filter((module: GspModule) => {
      return !ignoredModules.some(
        (ignored) =>
          ignored.serviceId === module.serviceId &&
          module.name.toLowerCase().includes(ignored.name.toLowerCase())
      );
    });

    const grouped = new Map<number, { common: number; specific: number }>();
    // Group modules
    filteredModules.forEach((module: GspModule) => {
      const serviceId: number = module.serviceId!;
      if (!grouped.has(serviceId)) {
        grouped.set(serviceId, { common: 0, specific: 0 });
      }

      const group = grouped.get(serviceId)!;
      if (module.isCommon) {
        group.common = Math.max(group.common, 1); // Count as 1 if any exists
      } else {
        group.specific++;
      }
    });

    // Convert to array and sort
    const result = Array.from(grouped.entries())
      .map(([serviceId, counts]) => ({
        serviceId,
        commonCount: counts.common,
        specificCount: counts.specific,
        moduleCount: (counts.common > 0 ? 1 : 0) + counts.specific,
      }))
      .sort((a, b) => a.serviceId - b.serviceId);
    // console.log('result cached groupded :>> ', result);
    return result;
  },

  /**
   * Get module count for specific service
   * Usage: this.$store.getters['gspModule/getModuleCountFor'](1) => 5
   */
  ['getModuleCountFor']: (state, getters) => (serviceId: number): number => {
    const group = getters['getModulesGroupedByService'].find(
      (g: ServiceModuleGroup) => g.serviceId === serviceId
    );
    return group?.moduleCount ?? 0;
  },

  /**
   * Get modules for specific service
   * Usage: this.$store.getters['gspModule/getModulesFor'](1)
   */
  ['getModulesFor']: (state) => (serviceId: number): GspModule[] => {
    return state.gspModules.items.filter(
      (m: GspModule) => m.serviceId === serviceId
    );
  },

  /**
   * Check if grouped modules cache is valid
   */
  ['isModulesGroupedCacheValid']: (state): boolean => {
    if (!state.modulesGroupedCache.lastFetchTime) return false;
    const elapsed = Date.now() - state.modulesGroupedCache.lastFetchTime;
    return elapsed < CACHE_CONFIG.DURATION;
  },

  /**
   * Get cache info (for debugging)
   */
  ['getModulesGroupedCacheInfo']: (state) => ({
    isValid: state.modulesGroupedCache.isValid,
    lastFetchTime: state.modulesGroupedCache.lastFetchTime,
    ageInSeconds: state.modulesGroupedCache.lastFetchTime
      ? Math.floor(
        (Date.now() - state.modulesGroupedCache.lastFetchTime) / 1000
      )
      : null,
  }),

};
